import { Suspense } from 'react';
import { Route, Navigate } from 'react-router';
import 'dayjs/locale/uk';
import axios from 'axios';
import { TCounterpartySelectorMode } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/types.ts';
import { SentryRoutes } from './sentry.tsx';
import { useRefreshToken } from './modules/auth/hooks/useRefreshToken.ts';
import FullPageLoader from './components/FullPageLoader/FullPageLoader.tsx';
import {
  LazyMainPage,
  LazyManagerLayout,
  LazySignIn,
  LazySignInManager,
  LazySignUp,
  LazyChangePassword,
  LazyApplicationReports,
  LazyPersonalDataMainPage,
  LazyPersonalDataRevokeCreateApplicationByClient,
  LazyPersonalDataCreateApplicationByClient,
  LazyPersonalDataCreateApplicationByManager,
  LazyPersonalDataApplication,
  LazyInvalidSessionMessage,
  LazyContactInformation,
  LazyRemoteBankingContactsAuthentication,
  LazyAssetManagerForm,
  LazyProtectedRoute,
  LazyProtectedGuaranteeRoute,
  LazyProtectedTasksRoute,
  LazyProtectedSupervisorAdministrationRoute,
  LazyMainForm,
  LazyDataUpdate,
  LazyCovenantsLayout,
  LazyCovenantsApplicationByManager,
  LazyManagerApplicationReviewPage,
  LazySegmentsManagerPage,
  LazyUsapokAdministrationPage,
  LazyUmvnAdministrationPage,
  LazyUoAdministrationPage,
  LazyUdbtfAdministrationPage,
  LazyCounterpartySelector,
  LazyCovenantsApplicationByClient,
  LazySupervisorContragentBlackListPage,
  LazyApplicationFormPage,
  LazyGuaranteesClientLayout,
  LazyGuaranteesCounterpartySelector,
  LazyGuaranteesClientPage,
  LazyGuaranteesApplicationPage,
  LazyGuaranteesApplicationStep2Page,
  LazyGuaranteesApplicationStep3Page,
  LazyGuaranteesManagerApplicationPage,
  LazyGuaranteesManagerApplicationStep2Page,
  LazyGuaranteesManagerApplicationStep3Page,
  LazyGuaranteesManagerApplicationStep4Page,
  LazyGuaranteesManagerUakdoControllerControlPage,
  LazyGuaranteesManagerUakdoControlPage,
  LazyGuaranteesManagerUdbtfGuaranteeScanCopyPage,
  LazyGuaranteesManagerUsapokApprovalPage,
  LazyGuaranteesManagerApplicationManualSignPage,
  LazyGuaranteesManagerApplicationClientSignedPage,
  LazyGuaranteesUdbtfApprovalLetterPage,
  LazyMyTasksPage,
  LazyNotFound,
} from './libs/lazyImport.ts';
import { useFeatureFlags } from './helpers/FeatureFlagsContext.tsx';
import { supervisorAdministrationPageConfigs } from './modules/supervisor-administration/config.ts';

axios.defaults.baseURL = import.meta.env.VITE_AXIOS_IDENTIFICATION_BASE_URL;

function App() {
  useRefreshToken();
  const { isGuaranteesEnabled, isTasksEnabled } = useFeatureFlags();

  return (
    <Suspense fallback={<FullPageLoader />}>
      <SentryRoutes>
        <Route path="/" element={<LazyMainPage />} />
        <Route path="/sign-in" element={<LazySignIn />} />
        <Route path="/manager/sign-in" element={<LazySignInManager />} />
        <Route path="/sign-up" element={<LazySignUp />} />
        <Route path="/change-password/:id/:token" element={<LazyChangePassword />} />
        <Route path="/services" element={<LazyMainPage />} />
        <Route
          path="/personal-data/revoke-application/new"
          element={<LazyPersonalDataRevokeCreateApplicationByClient />}
        />
        <Route
          path="/personal-data/application/new"
          element={<LazyPersonalDataCreateApplicationByClient />}
        />
        <Route path="/personal-data/application" element={<LazyPersonalDataApplication />} />
        <Route
          path="/personal-data/revoke-application"
          element={<LazyPersonalDataApplication isRevoke={true} />}
        />
        <Route path="/personal-data/application/expired" element={<LazyInvalidSessionMessage />} />
        <Route path="/remote-banking/contact-information" element={<LazyContactInformation />} />
        <Route
          path="/remote-banking-contacts/personal-link"
          element={<LazyRemoteBankingContactsAuthentication />}
        />
        <Route path="/identification" element={<LazySignIn />} />
        <Route
          path="/identification/asset-managers/questionnaire"
          element={<LazyAssetManagerForm />}
        />
        <Route
          path="/identification/asset-managers/questionnaire/expired"
          element={<LazyInvalidSessionMessage />}
        />
        <Route element={<LazyProtectedRoute />}>
          <Route path="/identification/questionnaire" element={<LazyMainForm />} />
        </Route>
        <Route element={<LazyProtectedRoute />}>
          <Route path="/data-update" element={<LazyDataUpdate />} />
        </Route>
        <Route element={<LazyProtectedRoute />}>
          <Route element={<LazyManagerLayout />}>
            <Route path="/application-reports" element={<LazyApplicationReports />} />
            <Route path="/personal-data-table" element={<LazyPersonalDataMainPage />} />
            <Route
              path="/personal-data/application/new-by-manager"
              element={<LazyPersonalDataCreateApplicationByManager />}
            />
            <Route path="/covenants/manager" element={<LazyCovenantsLayout />}>
              <Route path="applications" element={<LazyCovenantsApplicationByManager />} />
              <Route
                path="applications/:applicationId"
                element={<LazyManagerApplicationReviewPage />}
              />
              <Route path="segments" element={<LazySegmentsManagerPage />} />
              <Route
                path="email-blacklist"
                element={
                  <LazyCounterpartySelector mode={TCounterpartySelectorMode.EmailBlacklist} />
                }
              />
            </Route>
            {isGuaranteesEnabled && (
              <Route path="/administrator">
                <Route element={<LazyProtectedSupervisorAdministrationRoute />}>
                  <Route
                    path={supervisorAdministrationPageConfigs.usapok.routePath}
                    element={<LazyUsapokAdministrationPage />}
                  />
                  <Route
                    path={supervisorAdministrationPageConfigs.umvn.routePath}
                    element={<LazyUmvnAdministrationPage />}
                  />
                  <Route
                    path={supervisorAdministrationPageConfigs.uo.routePath}
                    element={<LazyUoAdministrationPage />}
                  />
                  <Route
                    path={supervisorAdministrationPageConfigs.udbtf.routePath}
                    element={<LazyUdbtfAdministrationPage />}
                  />
                </Route>
              </Route>
            )}
          </Route>
        </Route>
        <Route path="/covenant/table" element={<Navigate to="/covenants/table" replace />} />
        <Route path="/covenant/table" element={<Navigate to="/covenants/get-started" replace />} />
        <Route element={<LazyProtectedRoute />}>
          <Route path="/covenants" element={<LazyCovenantsLayout />}>
            <Route path="table" element={<Navigate to="/covenants/get-started" replace />} />
            <Route path="get-started" element={<LazyCounterpartySelector />} />
            <Route
              path="operator/get-started"
              element={<LazyCounterpartySelector mode={TCounterpartySelectorMode.Operator} />}
            />
            <Route
              path="covenants-branch-manager/get-started"
              element={
                <LazyCounterpartySelector mode={TCounterpartySelectorMode.CovenantsBranchManager} />
              }
            />
            <Route
              path="uakdo-administrator/get-started"
              element={
                <LazyCounterpartySelector mode={TCounterpartySelectorMode.UakdoAdministrator} />
              }
            />
            <Route path="contragents/:id/my" element={<LazyCovenantsApplicationByClient />} />
            <Route
              path="contragents/:id/black-list"
              element={<LazySupervisorContragentBlackListPage />}
            />
            <Route path="application/:applicationId" element={<LazyApplicationFormPage />} />
          </Route>
        </Route>
        <Route element={<LazyProtectedGuaranteeRoute />}>
          <Route path="/guarantees" element={<LazyGuaranteesClientLayout />}>
            <Route path="get-started" element={<LazyGuaranteesCounterpartySelector />} />
            <Route path="contragents/:id/my" element={<LazyGuaranteesClientPage />} />
            <Route path="application/:applicationId" element={<LazyGuaranteesApplicationPage />} />
            <Route
              path="application/:applicationId/step2"
              element={<LazyGuaranteesApplicationStep2Page />}
            />
            <Route
              path="application/:applicationId/step3"
              element={<LazyGuaranteesApplicationStep3Page />}
            />
          </Route>
          {isGuaranteesEnabled && (
            <Route element={<LazyManagerLayout />}>
              <Route path="/guarantees/administrator">
                <Route path="get-started" element={<LazyGuaranteesCounterpartySelector />} />
                <Route path="contragents/:id/my" element={<LazyGuaranteesClientPage />} />
                <Route
                  path="application/:applicationId"
                  element={<LazyGuaranteesManagerApplicationPage />}
                />
                <Route
                  path="application/:applicationId/step2"
                  element={<LazyGuaranteesManagerApplicationStep2Page />}
                />
                <Route
                  path="application/:applicationId/step3"
                  element={<LazyGuaranteesManagerApplicationStep3Page />}
                />
                <Route
                  path="application/:applicationId/step4"
                  element={<LazyGuaranteesManagerApplicationStep4Page />}
                />
                <Route
                  path="application/:applicationId/uakdo-control"
                  element={<LazyGuaranteesManagerUakdoControlPage />}
                />
                <Route
                  path="application/:applicationId/uakdo-controller-control"
                  element={<LazyGuaranteesManagerUakdoControllerControlPage />}
                />
                <Route
                  path="application/:applicationId/udbtf-guarantee-scan-copy"
                  element={<LazyGuaranteesManagerUdbtfGuaranteeScanCopyPage />}
                />
                <Route
                  path="application/:applicationId/usapok-approval"
                  element={<LazyGuaranteesManagerUsapokApprovalPage />}
                />
                <Route
                  path="application/:applicationId/am-approval-letter-manual-sign"
                  element={<LazyGuaranteesManagerApplicationManualSignPage />}
                />
                <Route
                  path="application/:applicationId/am-approval-letter-client-signed"
                  element={<LazyGuaranteesManagerApplicationClientSignedPage />}
                />
                <Route
                  path="application/:applicationId/udbtf-approval-letter"
                  element={<LazyGuaranteesUdbtfApprovalLetterPage />}
                />
                <Route
                  path="application/:applicationId/internal-approval-letter/:approvalType"
                  element={<LazyGuaranteesUdbtfApprovalLetterPage />}
                />
              </Route>
            </Route>
          )}
        </Route>
        {isTasksEnabled && (
          <Route element={<LazyProtectedTasksRoute />}>
            <Route element={<LazyManagerLayout />}>
              <Route path="/tasks" element={<LazyMyTasksPage />} />
            </Route>
          </Route>
        )}
        <Route path="*" element={<LazyNotFound />} />
      </SentryRoutes>
    </Suspense>
  );
}

export default App;
